import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

export interface EmailServerFormValues {
  smtp_host: string;
  smtp_port: string;
  smtp_secure: string;
  smtp_user: string;
  smtp_pass: string;
  smtp_from: string;
  smtp_tls_reject_unauthorized: string;
}

interface EmailServerConfigSectionProps {
  values: EmailServerFormValues;
  onChange: (field: keyof EmailServerFormValues, value: string) => void;
  onTest: () => void;
  testingSmtp: boolean;
  disabled?: boolean;
  onHelpClick: () => void;
  showSmtpPass: boolean;
  onToggleSmtpPass: () => void;
}

const EmailServerConfigSection: React.FC<EmailServerConfigSectionProps> = ({
  values,
  onChange,
  onTest,
  testingSmtp,
  disabled,
  onHelpClick,
  showSmtpPass,
  onToggleSmtpPass,
}) => {
  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 0 }}>
            Servidor de Email
          </Typography>
          <Tooltip title="Sobre o envio de e-mails">
            <IconButton size="small" color="primary" onClick={onHelpClick} sx={{ p: 0.25 }}>
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Configuração do SMTP para envio de mensagens pelo sistema (faturas, notificações, etc.). Também é possível usar variáveis de ambiente no backend como fallback.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Host SMTP"
          value={values.smtp_host}
          onChange={(e) => onChange('smtp_host', e.target.value)}
          placeholder="smtp.gmail.com"
          size="small"
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Porta"
          value={values.smtp_port}
          onChange={(e) => onChange('smtp_port', e.target.value.replace(/\D/g, ''))}
          placeholder="587"
          size="small"
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Switch
              checked={values.smtp_secure === 'true'}
              onChange={(_, c) => onChange('smtp_secure', c ? 'true' : 'false')}
              disabled={disabled}
            />
          }
          label="SSL/TLS (porta segura)"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Usuário"
          value={values.smtp_user}
          onChange={(e) => onChange('smtp_user', e.target.value)}
          placeholder="conta@dominio.com"
          size="small"
          disabled={disabled}
          autoComplete="off"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Senha / senha de app"
          type={showSmtpPass ? 'text' : 'password'}
          value={values.smtp_pass}
          onChange={(e) => onChange('smtp_pass', e.target.value)}
          size="small"
          disabled={disabled}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="alternar visibilidade da senha SMTP"
                  onClick={onToggleSmtpPass}
                  edge="end"
                  size="small"
                >
                  {showSmtpPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="E-mail remetente (From)"
          helperText='Com Gmail: use o mesmo e-mail do "Usuário" ou cadastre este endereço em Gmail > Configurações > Contas > Enviar e-mail como. Caso contrário o Gmail pode exibir o e-mail da conta autenticada.'
          value={values.smtp_from}
          onChange={(e) => onChange('smtp_from', e.target.value)}
          placeholder="noreply@empresa.com"
          size="small"
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={values.smtp_tls_reject_unauthorized === 'true'}
              onChange={(_, c) => onChange('smtp_tls_reject_unauthorized', c ? 'true' : 'false')}
              disabled={disabled}
            />
          }
          label="TLS: rejeitar certificados inválidos"
        />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={onTest}
            disabled={testingSmtp || disabled}
            size="small"
          >
            {testingSmtp ? 'Testando conexão...' : 'Testar conexão SMTP'}
          </Button>
        </Box>
      </Grid>
    </>
  );
};

export default EmailServerConfigSection;
